import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { Link } from "react-router-dom";

import "./FlowingMenu.css";

interface MenuItemProps {
  link: string;
  text: string | React.ReactNode;
  image: string;
  description?: string;
  id?: string;
}

interface FlowingMenuProps {
  items?: MenuItemProps[];
}

const FlowingMenu: React.FC<FlowingMenuProps> = ({ items = [] }) => {
  return (
    <div className="menu-wrap">
      <nav className="menu">
        {items.map((item, idx) => (
          <MenuItem key={idx} {...item} />
        ))}
      </nav>
    </div>
  );
};

const MenuItem: React.FC<MenuItemProps> = ({ link, text, image, description, id }) => {
  const itemRef = React.useRef<HTMLDivElement>(null);
  const marqueeRef = React.useRef<HTMLDivElement>(null);
  const marqueeInnerRef = React.useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const animationDefaults: gsap.TweenVars = { duration: 0.6, ease: "expo" };

  const distMetric = (x: number, y: number, x2: number, y2: number): number => {
    const xDiff = x - x2;
    const yDiff = y - y2;
    return xDiff * xDiff + yDiff * yDiff;
  };

  const findClosestEdge = (
    mouseX: number,
    mouseY: number,
    width: number,
    height: number,
  ): "top" | "bottom" => {
    const topEdgeDist = distMetric(mouseX, mouseY, width / 2, 0);
    const bottomEdgeDist = distMetric(mouseX, mouseY, width / 2, height);
    return topEdgeDist < bottomEdgeDist ? "top" : "bottom";
  };

  const emitExpandEvent = () => {
    if (id) {
      const section = id.split('-')[0];
      const event = new CustomEvent('menuItemExpand', {
        detail: { itemId: id, section }
      });
      window.dispatchEvent(event);
    }
  };

  useEffect(() => {
    emitExpandEvent();
  }, []);

  const emitCollapseEvent = () => {
    const event = new CustomEvent('menuItemCollapse');
    window.dispatchEvent(event);
  };

  const handleMouseEnter = (ev: React.MouseEvent<HTMLElement>) => {
    setIsHovered(true);
  };

  const handleMouseLeave = (ev: React.MouseEvent<HTMLElement>) => {
    setIsHovered(false);
  };

  const marqueeContent = React.useMemo(() => {
    return (
      <React.Fragment>
        <span>{text}</span>
        <div
          className="marquee__img"
          style={{ backgroundImage: `url(${image})` }}
        />
      </React.Fragment>
    );
  }, [text, image]);

  const isExternalLink = link.startsWith('http');

  return (
    <div 
      className={`menu__item ${isExpanded ? 'menu__item--expanded' : ''}`} 
      ref={itemRef}
      data-id={id}
    >
      {isExternalLink ? (
        <a
          className="menu__item-link"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {text}
        </a>
      ) : (
        <Link
          className="menu__item-link"
          to={link}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {text}
        </Link>
      )}
      <div className={`marquee ${isHovered ? 'marquee--animating' : ''}`} ref={marqueeRef}>
        <div className="marquee__inner-wrap" ref={marqueeInnerRef}>
          <div className={`marquee__inner ${isHovered ? 'marquee__inner--animating' : ''}`} aria-hidden="true">
            {marqueeContent}
          </div>
        </div>
      </div>
      
      {description && (
        <div 
          className={`menu__item-description ${isExpanded ? 'menu__item-description--visible' : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <p>{description}</p>
        </div>
      )}
    </div>
  );
};

export default FlowingMenu;
